import { supabase } from '@/integrations/supabase/client';

const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

export class UploadError extends Error {
  constructor(message: string, public code: 'type' | 'size' | 'network' | 'server') {
    super(message);
  }
}

function validate(file: File) {
  if (!ALLOWED.includes(file.type)) {
    throw new UploadError('Only PNG, JPEG, WEBP, or GIF images are supported.', 'type');
  }
  if (file.size > MAX_BYTES) {
    throw new UploadError('Image is too large. Maximum size is 5 MB.', 'size');
  }
}

export async function uploadForumImage(file: File): Promise<string> {
  validate(file);
  const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
  const path = `anonymous/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from('forum-images')
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw new UploadError(error.message || 'Upload failed.', 'server');
  const { data } = supabase.storage.from('forum-images').getPublicUrl(path);
  return data.publicUrl;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

/**
 * Uploads a forum image with real progress events via XHR.
 * Returns the resulting public URL.
 */
export function uploadForumImageWithProgress(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  validate(file);
  const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
  const path = `anonymous/${crypto.randomUUID()}.${ext}`;
  const url = `${SUPABASE_URL}/storage/v1/object/forum-images/${path}`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${SUPABASE_KEY}`);
    xhr.setRequestHeader('apikey', SUPABASE_KEY);
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onerror = () => reject(new UploadError('Network error — please try again.', 'network'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const { data } = supabase.storage.from('forum-images').getPublicUrl(path);
        onProgress?.(100);
        resolve(data.publicUrl);
      } else {
        let msg = `Upload failed (${xhr.status}).`;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body?.message) msg = body.message;
        } catch { /* ignore */ }
        reject(new UploadError(msg, 'server'));
      }
    };

    xhr.send(file);
  });
}
